/**
 * Documented examples, not a deployed-program or live-chain assertion.
 * Stages transcribed from the CTO's planStages output at the pinned source.
 * Each stage groups independent changes; it does not specify transaction counts.
 */
export const GOVERNANCE_WORKFLOW_SOURCE =
    "https://github.com/SolomonLabs/chancery-governance-flows/blob/b601eed560fb43e01fc6cd06d995e61dce43d1bb/chancery-governance-flows.mjs";

interface WorkflowStageDetails {
    title: string;
    /** UI label; the source title above remains the CTO's stage wording. */
    tabLabel?: string;
    changes: readonly string[];
    prerequisite?: string;
}

export type GovernanceWorkflowStage = WorkflowStageDetails &
    (
        | {
              mode: "direct";
              authority: "Operations" | "Emergency" | "Enforcement";
              waitHours: 0;
          }
        | {
              mode: "governed";
              authority: "Governance";
              waitHours: 2 | 8 | 24;
          }
    );

export interface GovernanceWorkflow {
    id: string;
    label: string;
    outcome: string;
    note: string;
    /** Reader-facing caption shown under the route on the Security tab. */
    description: string;
    stages: readonly GovernanceWorkflowStage[];
}

export const GOVERNANCE_WORKFLOWS: readonly GovernanceWorkflow[] = [
    {
        id: "emergency-stop",
        label: "Emergency pause",
        outcome: "Pause the settlement module.",
        note: "Emergency can pause a module. Only Governance can restart it.",
        description: "Stop affected settlement immediately. Restarting follows a separate governance path.",
        stages: [
            {
                title: "Pause program",
                changes: ["Settlement module"],
                mode: "direct",
                authority: "Emergency",
                waitHours: 0
            }
        ]
    },
    {
        id: "basic-freeze",
        label: "Freeze an account",
        outcome: "Freeze two issued-token accounts so their tokens cannot move.",
        note: "Separate account records allow independent freezes. Emergency can also freeze, but cannot thaw.",
        description:
            "Immobilize a selected USDv token account without changing its balance or owner. Thawing is a separate action.",
        stages: [
            {
                title: "Freeze account",
                changes: ["First account", "Second account"],
                mode: "direct",
                authority: "Enforcement",
                waitHours: 0
            }
        ]
    },
    {
        id: "limit-bump",
        label: "Raise a limit",
        outcome: "Raise the caps on three independent limit policies.",
        note: "The three policies can share a wait because they bind different accounts.",
        description:
            "Increasing a cap requires an exact proposal and at least a two-hour program delay. Lowering a cap can happen immediately.",
        stages: [
            {
                title: "Raise policy caps",
                changes: ["Pathway policy", "Counterparty policy", "Executor policy"],
                mode: "governed",
                authority: "Governance",
                waitHours: 2
            }
        ]
    },
    {
        id: "grant-pathway-access",
        label: "Grant access",
        outcome: "Allow two wallets to mint and redeem on an existing pathway.",
        note: "This example grants ordinary pathway access. A grant carrying a dangerous role has a minimum program wait of 24 hours.",
        description:
            "Permission applies to a defined wallet, action, and pathway. An ordinary grant waits at least two hours; a dangerous grant waits at least 24.",
        stages: [
            {
                title: "Grant pathway permissions",
                changes: ["First wallet permission", "Second wallet permission"],
                mode: "governed",
                authority: "Governance",
                waitHours: 2
            }
        ]
    },
    {
        id: "emergency-recovery",
        label: "Restart a module",
        outcome: "Restart the settlement module after an emergency pause.",
        note: "Governance must perform this recovery on its own. Restarting the cross-chain module has a minimum program wait of 24 hours. Token unpause is a separate action.",
        description:
            "Recovering settlement after an emergency shutdown requires Governance and the applicable waiting period.",
        stages: [
            {
                title: "Restart settlement",
                changes: ["Settlement module"],
                mode: "governed",
                authority: "Governance",
                waitHours: 8
            }
        ]
    },
    {
        id: "reserve-withdrawal-access",
        label: "Open a reserve destination",
        outcome: "Register a payout destination and authorize two callers to withdraw to it.",
        note: "Registration and activation change the same destination account. Applying registration changes its stored state, so activation needs a new proposal and a second wait. This configures withdrawal access; it does not move reserves.",
        description:
            "Register the destination disabled, wait at least 24 hours, then propose its activation and wait at least two more hours. Asset bindings and withdrawal caps still apply.",
        stages: [
            {
                title: "Register destination and callers",
                tabLabel: "1. Register Destination",
                changes: ["Payout destination", "First caller permission", "Second caller permission"],
                mode: "governed",
                authority: "Governance",
                waitHours: 24
            },
            {
                title: "Enable the destination",
                tabLabel: "2. Enable Destination",
                changes: ["Payout destination"],
                prerequisite: "After registration is applied",
                mode: "governed",
                authority: "Governance",
                waitHours: 2
            }
        ]
    },
    {
        id: "counterparty-onboarding",
        label: "Add a counterparty",
        outcome: "Set up a new minter with its own limits and pathway permission.",
        note: "Operations prepares the policies. Governance grants the final permission after a program wait. Direct stages still need approval and execution.",
        description:
            "Approve the relevant pathway, asset, limits, and permissions before that counterparty can settle.",
        stages: [
            {
                title: "Set up limits",
                tabLabel: "1. Add Minter Limit",
                changes: ["Pathway limits", "Counterparty limits"],
                mode: "direct",
                authority: "Operations",
                waitHours: 0
            },
            {
                title: "Create the pathway",
                tabLabel: "2. Add Minter Pathway",
                changes: ["Pathway policy"],
                prerequisite: "After both limit policies are created",
                mode: "direct",
                authority: "Operations",
                waitHours: 0
            },
            {
                title: "Set collateral rules",
                tabLabel: "3. Add Collateral Rules",
                changes: ["Pathway policy"],
                prerequisite: "After the pathway is created",
                mode: "direct",
                authority: "Operations",
                waitHours: 0
            },
            {
                title: "Grant the permission",
                tabLabel: "4. Register Permissions",
                changes: ["Counterparty permission"],
                prerequisite: "After collateral rules are applied; Governance takes over",
                mode: "governed",
                authority: "Governance",
                waitHours: 2
            }
        ]
    },
    {
        id: "asset-activation",
        label: "Activate an asset",
        outcome: "Prepare an asset for settlement, set its caps, and activate it.",
        note: "Caps and activation change the same asset account. Governance must apply the caps before proposing activation, so the two program waits add together.",
        description:
            "Configure its settlement path and limits before accepting it for issuance. Activation is a separate risk-increasing decision.",
        stages: [
            {
                title: "Prepare the policies",
                tabLabel: "1. Add Asset Policies",
                changes: ["Limit policy", "Fee policy", "Evidence policy"],
                mode: "direct",
                authority: "Operations",
                waitHours: 0
            },
            {
                title: "Create the pathway",
                tabLabel: "2. Add Asset Pathway",
                changes: ["Pathway policy"],
                prerequisite: "After all three policies are created",
                mode: "direct",
                authority: "Operations",
                waitHours: 0
            },
            {
                title: "Set asset caps",
                tabLabel: "3. Add Asset Caps",
                changes: ["Asset configuration"],
                prerequisite: "After the pathway is created; Governance takes over",
                mode: "governed",
                authority: "Governance",
                waitHours: 2
            },
            {
                title: "Activate the asset",
                tabLabel: "4. Register Asset",
                changes: ["Asset configuration"],
                prerequisite: "After asset caps are applied",
                mode: "governed",
                authority: "Governance",
                waitHours: 2
            }
        ]
    }
];
